import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Container,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Form, FormikProvider, useFormik } from 'formik';
import { useStyles } from '../styles/employeeFormModalStyles';
import Iconify from '../../../components/iconify';
import { ADD_CLIENT_FORM_TITLE, EDIT_CLIENT_FORM_TITLE, INITIAL_FORM_STATE } from '../constants';
import EmployeeSchema from '../schema';

const EmployeeFormModal = ({ open, onClose, selectedRow, onSubmit, action, userRoleList }) => {
  const classes = useStyles();

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    await onSubmit(values);
    setSubmitting(false);
  };

  let initialValues;
  if (action === 'create') {
    initialValues = INITIAL_FORM_STATE;
  } else if (action === 'update') {
    const userName = selectedRow?.userName;
    const email = selectedRow?.email;
    const userRoleId = selectedRow?.userRoleId;
    initialValues = { userName, email, userRoleId };
  }

  const formik = useFormik({
    initialValues,
    validationSchema: EmployeeSchema,
    onSubmit: handleSubmit,
  });

  const { errors, touched, isSubmitting, submitForm, setFieldValue, getFieldProps, resetForm } = formik;

  const handleClose = () => {
    onClose();
    resetForm({ values: INITIAL_FORM_STATE });
  };

  let headerText = '';
  if (action === 'create') {
    headerText = ADD_CLIENT_FORM_TITLE;
  } else if (action === 'update') {
    headerText = EDIT_CLIENT_FORM_TITLE;
  }
  return (
    <Modal className={classes.modal} open={open} onClose={handleClose}>
      <Box className={classes.root}>
        <Paper className={classes.paper}>
          <Box className={classes.modalHeader}>
            <Typography variant="h5" className={classes.headerText}>
              {headerText}
            </Typography>
            <IconButton color="primary" onClick={handleClose}>
              <Iconify icon="ic:round-clear" />
            </IconButton>
          </Box>
          <Divider className={classes?.divider} />
          <FormikProvider value={formik}>
            <Form onSubmit={formik?.handleSubmit}>
              <Container maxWidth="sm">
                <Stack spacing={2}>
                  <TextField
                    autoFocus
                    label="Name"
                    {...getFieldProps('userName')}
                    fullWidth
                    error={touched.userName && Boolean(errors.userName)}
                    helperText={touched.userName && errors.userName}
                  />
                  <TextField
                    label="Email"
                    {...getFieldProps('email')}
                    disabled={action === 'update'}
                    fullWidth
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                  />
                  <FormControl fullWidth error={touched.userRoleId && Boolean(errors.userRoleId)}>
                    <InputLabel>Role</InputLabel>
                    <Select
                      {...getFieldProps('userRoleId')}
                      label="Role"
                      onChange={(event, value) => {
                        setFieldValue('userRoleId', value?.props?.value);
                      }}
                    >
                      {userRoleList.map((role, index) => (
                        <MenuItem value={role.id} key={index}>
                          {role.roleName}
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.userRoleId && errors.userRoleId && (
                      <FormHelperText style={{ color: 'red' }}>{errors.userRoleId}</FormHelperText>
                    )}
                  </FormControl>
                  <LoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={submitForm}
                    loading={isSubmitting}
                  >
                    Save
                  </LoadingButton>
                </Stack>
              </Container>
            </Form>
          </FormikProvider>
        </Paper>
      </Box>
    </Modal>
  );
};

EmployeeFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedRow: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  action: PropTypes.string.isRequired,
  userRoleList: PropTypes.array.isRequired,
};
export default EmployeeFormModal;
